import React, {Component, Fragment} from 'react';
import {inject, observer} from 'mobx-react';
import {action, observable, reaction, runInAction} from 'mobx';
import {withStyles} from '@material-ui/core/styles';
import MdAttachFile from 'react-icons/lib/md/attach-file';
import {FormattedMessage, injectIntl} from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import isUndefined from 'lodash/isUndefined';
import classNames from 'classnames';
import LessonInfoVideo from './LessonInfoVideo';
import {actions, category, DialogTypes, events, LessonState, ProjectStatus, ProjectType, TrackPlayStatus} from '../../../constants/enums';
import SvgIconButton from '../../../components/SvgIconButton';
import {primary, white} from '../../../themes/colors';
import CircularLoaderInline from '../../../components/circularLoader/CircularLoaderInline';
import LessonInfoForm from './LessonInfoForm';
import LessonInfoPreview from './LessonInfoPreview';
import {getUser, isAdmin, isStudent, isTeacher} from '../../../utils/userUtility';
import LessonVideoRecorder from './LessonVideoRecorder';
import infoIcon from '../../../assets/images/info';
import CSSTransition from '../../../components/CSSTransition';
import {go} from '../../../utils/navigationUtility';
import {customEvent} from '../../../utils/analyticsUtil';
import by from '../../../assets/images/img-by.png';
import BaseButton from '../../../components/button/BaseButton';
import LessonVideoPreviewDrawer from './LessonVideoPreviewDrawer';
import LessonVideoLinkDrawer from './LessonVideoLinkDrawer';
import Photo from '../../../assets/images/photo';
import UploadImageDialog from '../../main/changeUserAvatarDialog/UploadImageDialog';
import LessonDefaultImage from '../../../assets/images/bg-lesson.png';
import {ellipsisString} from '../../../utils/commonUtility';
import './lessonInfo.scss';
import {withWiki} from '../../../compose/withWiki';
import FileAttachList from "../../../components/FileAttach/FileAttachList";

const infoIconStyle = {
    width: '10px',
    height: '10px',
    minWidth: '10px',
    minHeight: '10px',
    borderRadius: 0
};

const styles = () => ({
    dialog: {
        zIndex: 1400
    },
    paper: {
        boxShadow: 'none',
        width: 1000,
        maxWidth: 'calc(100vw - 48px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'block',
        borderRadius: 16,
        maxHeight: 'calc(100% - 28px)',
        margin: 0,
        backgroundColor: white
    }
});

@withWiki
@injectIntl
@inject('looperStore', 'projectStore', 'appStore', 'dialogStore', 'manageStore', 'mainStore', 'actionStore')
@observer
class LessonInfoEditor extends Component {
    @observable projectName = '';
    @observable isOpenRecorder = false;
    @observable isOpenVideoLinkDrawer = false;
    @observable isSaveProcessing = false;
    @observable error = '';
    @observable isSaveLesson = false;
    @observable stream = null;
    @observable form = {
        instruments: [],
        level: '',
        category: '',
        musicalKey: '',
        keywords: '',
        artistName: '',
        isPublishTeachers: false,
        isPublishStudents: false,
        includeInDemoStudent: false,
        isShareable: true
    };

    componentDidMount() {
        const {looperStore, projectStore, mainStore} = this.props;
        const {isLessonEditing, editedLesson} = projectStore;
        if (isLessonEditing) {
            this.handleLoopedProjectChange(editedLesson);
            return;
        }
        if (projectStore.activeProject && projectStore.activeProject.type === ProjectType.Global) {
            const project = !mainStore.isHomeMode ? looperStore.loopedProject : projectStore.activeProject;
            this.handleLoopedProjectChange(project);
            return;
        }
        this.initLessonInfoForm();
        reaction(
            () => looperStore.loopedProject,
            loopedProject => this.handleLoopedProjectChange(loopedProject),
            {fireImmediately: true}
        );
        reaction(
            () => projectStore.activeProject,
            activeProject => {
                if (activeProject) {
                    this.handleLoopedProjectChange(activeProject)
                }
            },
            {}
        );
    }

    @action initLessonInfoForm = () => {
        this.form = {description: ''};
        if (isAdmin()) {
            this.form = {
                ...this.form,
                level: '',
                category: '',
                instruments: [],
                labels: [],
                musicalKey: '',
                keywords: '',
                artistName: '',
                isPublishTeachers: false,
                isPublishStudents: false,
                includeInDemoStudent: false,
                isShareable: true
            };
        }
    };

    @action toggleVideoLinkDrawer = () => {
        this.isOpenVideoLinkDrawer = !this.isOpenVideoLinkDrawer;
    };

    @action toggleRecorder = () => {
        this.isOpenRecorder = !this.isOpenRecorder;
    };

    @action setProcessing = (val) => {
        this.isSaveProcessing = val;
        if (this.props.onUploadStatusChange) {
            this.props.onUploadStatusChange(val);
        }
    };

    onSaveVideo = async (file) => {
        const {projectStore} = this.props;
        this.setProcessing(true);
        if (!isObject(file)) {
            projectStore.saveVideo(file);
            setTimeout(() => {
                this.setProcessing(false);
            }, 0);
            return;
        }
        if (this.props.onUploadPercentChange) {
            await projectStore.uploadVideoToS3(file, this.onUploadProgress);
        } else {
            await projectStore.uploadVideoToS3(file);
        }
        runInAction(() => {
            this.isOpenRecorder = false;
            this.setProcessing(false);
            if (this.props.onUploadPercentChange) {
                this.props.onUploadPercentChange(0);
            }
        });
    };

    @action onUploadProgress = (fileName, {total, loaded}) => {
        this.props.onUploadPercentChange(Math.round((loaded / total) * 100));
    };

    @action onRecordOpen = async () => {
        this.error = '';
        try {
            this.stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: true
            });
            this.toggleRecorder();
        } catch (e) {
            runInAction(() => {
                this.error = <FormattedMessage id="createLesson.errorMedia"/>;
            });
        }
    };

    @action stopStream = () => {
        if (this.stream) this.stream.getTracks().forEach(track => track.stop());
    };

    @action onChangeName = (e) => {
        const {isStudioModeEditor, looperStore} = this.props;
        if (isStudioModeEditor) looperStore.setProjectName(e.target.value);
        this.projectName = e.target.value;
        this.error = '';
        this.isSaveLesson = false;
    };

    @action onChangeDescription = (val) => {
        const {isStudioModeEditor, looperStore} = this.props;
        if (this.form.description === val) return;
        this.form.description = val;
        if (isStudioModeEditor) looperStore.setProjectDescription(val);
    };

    @action togglePlayActiveProject = () => {
        this.props.projectStore.togglePlayActiveProject();
    };

    updateProjectForm = () => {
        const {isStudioModeEditor, looperStore} = this.props;
        if (isStudioModeEditor) looperStore.updateProjectForm(this.form);
    };

    @action handleLoopedProjectChange = ({
        name = '', description = '', level = '', category = '', videoUrls = [],
        instruments = [], musicalKey = '', keywords = '', artistName = '', labels = [],
        isPublishTeachers = false, isPublishStudents = false, includeInDemoStudent = false, isShareable = true
    }) => {
        this.projectName = name;
        this.form.description = description;
        if (videoUrls.length !== this.props.projectStore.videos.length) {
            this.props.projectStore.initVideos(videoUrls);
        }
        if (isAdmin()) {
            this.form = {...this.form, level, category, instruments, musicalKey, keywords, artistName,
                isPublishTeachers, isPublishStudents, includeInDemoStudent, labels, isShareable};
        }
    };

    @action validateTitle = () => {
        if (!this.projectName) {
            this.error = <FormattedMessage id="createLesson.errorTitle"/>;
            this.isSaveLesson = true;
            return false;
        }
        return true;
    };

    @action handleSaveClick = async () => {
        if (!this.validateTitle()) return;
        const {looperStore, projectStore} = this.props;
        const project = looperStore.loopedProject;
        project.status = ProjectStatus.Private;
        project.type = ProjectType.Lesson;
        //project.actions = actionStore.getAll();
        project.description = this.form.description;
        project.isNew = true;
        if (isAdmin()) {
            this.setProjectAdminParams(project, this.form);
        }
        await projectStore.saveProject(this.projectName || 'Untitled Lesson', project, true);
        customEvent(events.CustomEvent, {category: category.Lesson, action: actions.CreateNewLesson});
        projectStore.updateLessonState(LessonState.Update);
        go('/create', {}, true);
        this.closeInfoDialog();
        looperStore.showTooltip();
    };

    @action handleUpdateClick = async () => {
        if (!this.validateTitle()) return;
        const {projectStore, appStore, looperStore, actionStore} = this.props;
        const {isLessonEditing, editedLesson, finishEditLesson} = projectStore;
        const project = isLessonEditing ? editedLesson : looperStore.loopedProject;
        try {
            const actions = actionStore.getAll();
            project.actions = actions.length ? actions : project.actions;
            project.status = project.status || ProjectStatus.Private;
            project.description = this.form.description || project.description;
            if (isAdmin()) {
                this.setProjectAdminParams(project, this.form);
            }
            await projectStore.saveProject(this.projectName || 'Untitled Lesson', project);
            finishEditLesson();
            this.closeInfoDialog();
            appStore.createAlertFromMessageId('success', isStudent() ? 'notifications.updateProject' : 'notifications.updateLesson');
        } catch (err) {
            appStore.createAlertFromMessageId('error', 'notifications.saveError');
        }
    };

    handleCreateLessonFromPlan = () => {
        this.props.projectStore.createLessonFromPlan();
        this.closeInfoDialog();
    };

    setProjectAdminParams = (project, form) => {
        project.type = ProjectType.Global;
        project.isPublishTeachers = form.isPublishTeachers;
        project.isPublishStudents = form.isPublishStudents;
        project.includeInDemoStudent = form.includeInDemoStudent;
        project.isShareable = form.isShareable;
        if (isUndefined(project.description) || isEmpty(project.description)) {
            project.description = '<p></p>';
        }
        if (form.category !== '') {
            project.category = form.category;
        }
        if (form.level !== '') {
            project.level = form.level;
        }
        if (form.musicalKey !== '') {
            project.musicalKey = form.musicalKey;
        }
        if (!isEmpty(project.instruments) || !isEmpty(form.instruments)) {
            project.instruments = form.instruments;
        }
        if (form.keywords !== '') {
            project.keywords = form.keywords;
        }
        if (form.artistName !== '') {
            project.artistName = form.artistName;
        }
        if (!isEmpty(form.labels)) {
            project.labels = form.labels;
        } else {
            project.labels = [];
        }
    };

    closeInfoDialog = () => {
        const {dialogStore, projectStore} = this.props;
        if (this.isSaveProcessing) return;
        if (projectStore.isLessonEditing) {
            projectStore.finishEditLesson();
        }
        if (projectStore.activeProject && projectStore.activeProject.type === ProjectType.Global) {
            projectStore.clearActiveProject();
        }
        projectStore.stopAll();
        dialogStore.closeDialog(DialogTypes.LessonInfo);
    };

    openEditLessonImage = project => () => {
        this.props.projectStore.openLessonBackgroundEditing(project);
    };

    renderTeacherFiles = () => {
        const {attachmentFiles, onFileChange, onFileDelete, openFilePreview, isFileLoading} = this.props.projectStore;
        return <FileAttachList
            files={attachmentFiles.slice()}
            fileExtension="image/*,.doc,.docx,.pdf,application/pdf"
            onFileChange={onFileChange}
            onFileDelete={onFileDelete}
            openFilePreview={openFilePreview}
            isFileLoading={isFileLoading}/>;
    };

    renderStudentFiles = () => {
        const {attachmentFiles, openFilePreview} = this.props.projectStore;
        return <Fragment>
            {attachmentFiles.map((file, id) => {
                return <a onClick={openFilePreview(file)} download key={`${file.name}${id}`} className="publish-attach-file link">
                    <MdAttachFile style={{fontSize: 18, position: 'absolute', left: 3, top: 3, fill: primary, transform: 'rotate(45deg)'}}/>
                    {file.name}
                </a>;
            })}
        </Fragment>;
    };

    renderSaveButton = () => {
        const {projectStore, intl: {formatMessage: translate}} = this.props;
        const onClick = projectStore.lessonState === LessonState.Create ? this.handleSaveClick : this.handleUpdateClick;
        return <div className="lesson-modal-footer"><BaseButton className="lesson-btn"
                                                                label={translate({id: projectStore.lessonState === LessonState.Create ? 'createLesson.create' : 'createLesson.save'})}
                                                                disabled={this.props.projectStore.isProcessing || this.isSaveLesson} onClick={onClick}
        /></div>;
    };

    renderLessonPlanBottomSection = () => {
        const {intl: {formatMessage: translate}} = this.props;
        return (
            <div className="lesson-modal-footer">
                <a onClick={this.closeInfoDialog}><FormattedMessage id={`createLesson.backTo${isTeacher() ? 'LessonPlans' : 'Discovery'}`}/></a>
                <div className="save-buttons">
                    {isTeacher() && <BaseButton
                        className="lesson-btn" label={translate({id: 'createLesson.saveLessonPlanAndOpen'})} onClick={this.handleCreateLessonFromPlan}/>}
                    {isStudent() && <BaseButton
                        className="lesson-btn" label={translate({id: 'createLesson.enrollLessonPlan'})}
                        onClick={this.handleCreateLessonFromPlan}/>}
                </div>
            </div>
        );
    };

    getProjectPublisher = (project) => {
        const user = getUser();
        let result;
        if (isUndefined(project.id)) {
            result = project.parentOwnerId === user.id || (isUndefined(project.parentOwnerId) && isUndefined(project.parentProjectId)) ?
                user : this.props.manageStore.getTeacher(project.parentOwnerId);
        } else {
            result = (!isStudent() && project.type !== ProjectType.Global) || project.type === ProjectType.Project ?
                user : this.props.manageStore.getTeacher(project.parentOwnerId || project.userId);
        }
        return result;
    }

    render() {
        const {projectStore, looperStore, mainStore, appStore, uploadStatus, uploadPercent, isStudioModeEditor = false, intl: {formatMessage: translate}} = this.props;
        const {
            files, editedLesson, activeProject, openVideoPreview, closeVideoPreview, isSavingBackground, saveLessonBackground,
            isProcessing, videoUrls, removeVideo, openedVideo, isLessonBackgroundEditing, closeLessonBackgroundEditing, isFetching
        } = projectStore;
        const {loopedProject} = looperStore;
        const project = editedLesson && editedLesson.id ? editedLesson : (activeProject ? activeProject : loopedProject);
        const isGlobal = projectStore.isGlobalProject(project.type);
        const backgroundImg = mainStore.isHomeMode ? (editedLesson && editedLesson.backgroundImg) || (activeProject && activeProject.backgroundImg) : loopedProject && loopedProject.backgroundImg;
        const isPreview = projectStore.isPreviewProject(project.type);
        const title = `${isStudent() ? 'createProject.info' : 'createLesson.info'}`;
        const publisher = this.getProjectPublisher(project);
        const modalClasses = classNames({
            'lesson-modal': true,
            global: isGlobal,
            'open-recorder': this.isOpenRecorder || openedVideo || this.isOpenVideoLinkDrawer
        });

        return (
            <Fragment>
                {uploadStatus && <div className="circular-loader-wrap">
                    <div className="circular-loader"/>
                    <div className="circular-loader-percent">
                        <FormattedMessage id="general.uploading"/> {uploadPercent}%
                    </div>
                </div>}
                {!isStudioModeEditor &&
                <div className="lesson-modal-title">
                    <FormattedMessage id={isPreview ? 'createLesson.preview' : title}/>
                </div>
                }
                <div className={modalClasses}>
                    <CSSTransition animation="slide-panel" timeout={{enter: 500, leave: 500}}>
                        {this.isOpenRecorder && <LessonVideoRecorder
                            isSaveProcessing={this.isSaveProcessing}
                            onSave={this.onSaveVideo}
                            stopStream={this.stopStream}
                            stream={this.stream}
                            toggleRecorder={this.toggleRecorder}/>}
                    </CSSTransition>
                    <CSSTransition animation="slide-panel" timeout={{enter: 500, leave: 500}}>
                        {openedVideo ? <LessonVideoPreviewDrawer
                            src={openedVideo}
                            closePreview={closeVideoPreview}/> : null}
                    </CSSTransition>
                    <CSSTransition animation="slide-panel" timeout={{enter: 500, leave: 500}}>
                        {this.isOpenVideoLinkDrawer ? <LessonVideoLinkDrawer
                            onSaveVideo={this.onSaveVideo}
                            createAlertFromMessageId={appStore.createAlertFromMessageId}
                            closePreview={this.toggleVideoLinkDrawer}/> : null}
                    </CSSTransition>
                    <div className="lesson-top">
                        {isGlobal ?
                            <Fragment>
                                <div className="lesson-top-info">
                                    <div className="image"><img src={project.backgroundImg} alt=""/></div>
                                    <div className="title">
                                        {(this.projectName === 'Untitled Lesson') ? '' : ellipsisString(this.projectName, 50)}
                                    </div>
                                </div>
                                {publisher && <div className="lesson-author">
                                    <img className="lesson-by" src={by} alt=""/>{publisher.name}
                                </div>}
                            </Fragment>
                            :
                            <Fragment>
                                <div className={`lesson-image ${backgroundImg ? 'has-image' : ''}`}
                                     onClick={isPreview ? null : this.openEditLessonImage(project)}>
                                    {backgroundImg && <img src={backgroundImg} alt=""/>}
                                    {!isPreview && <SvgIconButton
                                        viewBox="0 0 20 20"
                                        icon={Photo}
                                        containerClass="change-lesson-image"
                                    />}
                                </div>
                                <div className="lesson-main-info">
                                    <input
                                        readOnly={isPreview}
                                        autoFocus={mainStore.focusLessonTitle}
                                        className="lesson-title-input"
                                        placeholder={translate({id: 'createLesson.titlePlaceholder'})}
                                        value={(this.projectName === 'Untitled Lesson') ? '' : this.projectName}
                                        onChange={this.onChangeName}
                                    />
                                    {publisher && publisher.name && <div className="lesson-author">
                                        <img className="lesson-by" src={by} alt=""/>{publisher.name}
                                    </div>}
                                </div>
                            </Fragment>
                        }
                    </div>
                    <div className="lesson-content">
                        {!isPreview && <div className="lesson-description">
                            <LessonInfoForm
                                form={this.form}
                                owner={publisher}
                                readMore={!mainStore.isHomeMode}
                                projectName={this.projectName}
                                onChangeDescription={this.onChangeDescription}
                                onChangeName={this.onChangeName}
                                updateProjectForm={this.updateProjectForm}/>
                            <div className="lesson-btn-wrap">
                                {this.error && <div className="file-input-error">
                                    <SvgIconButton
                                        viewBox="0 0 12 12"
                                        icon={infoIcon}
                                        buttonStyle={infoIconStyle}
                                        hoverColor="none" rippleColor="none"
                                        iconStyle={infoIconStyle}
                                        containerClass="file-input-error-icon"/>{this.error}</div>}
                            </div>
                        </div>}
                        {isPreview && <div className="lesson-description">
                            <LessonInfoPreview
                                readMore={!mainStore.isHomeMode}
                                projectName={this.projectName}
                                description={this.props.makeWikiString(this.form.description || '')}
                                togglePlay={this.togglePlayActiveProject}
                                isPlaying={projectStore.activeProject && projectStore.activeProject.playStatus === TrackPlayStatus.Playing}
                                showPlay={isGlobal}
                            />
                        </div>}
                        <div className="lesson-details">
                            {(videoUrls.length || !isPreview) && <LessonInfoVideo
                                videoList={videoUrls}
                                isSaveProcessing={this.isSaveProcessing}
                                disabled={this.isSaveProcessing}
                                isEditable={!isPreview}
                                onSave={this.onSaveVideo}
                                onRecordOpen={this.onRecordOpen}
                                onRemove={removeVideo}
                                openVideoPreview={openVideoPreview}
                                onVideoLinkOpen={this.toggleVideoLinkDrawer}
                            />}
                            <div className="file-input-wrapper">
                                {(!!files.length || !isPreview) && <label className="lesson-label">
                                    <FormattedMessage id="createLesson.attachments"/>
                                </label>}
                                {!isPreview ? this.renderTeacherFiles() : this.renderStudentFiles()}
                            </div>
                        </div>
                    </div>
                </div>
                {isGlobal && this.renderLessonPlanBottomSection()}
                {!isGlobal && !isStudioModeEditor && this.renderSaveButton()}
                {(isProcessing || isFetching) && <div className="loader-container"><CircularLoaderInline style={{margin: '26px 0 0'}}/></div>}
                {isLessonBackgroundEditing && <UploadImageDialog
                    avatar={backgroundImg || LessonDefaultImage}
                    isSavingBackground={isSavingBackground}
                    title='modalTitle'
                    open={isLessonBackgroundEditing}
                    onSave={saveLessonBackground}
                    onRequestClose={closeLessonBackgroundEditing}
                />}
            </Fragment>
        );
    }
}

export default withStyles(styles)(LessonInfoEditor);
